/**
 * Сервис для управления версионированием приложения
 * и принудительного обновления кеша при изменении версии
 */

class VersionService {
  constructor() {
    this.currentVersion = process.env.VUE_APP_VERSION || '1.0.0'
    this.storageKey = 'app_version'
  }

  /**
   * Получить текущую версию приложения
   */
  getCurrentVersion() {
    return this.currentVersion
  }

  /**
   * Получить сохраненную версию из localStorage
   */
  getStoredVersion() {
    return localStorage.getItem(this.storageKey)
  }

  /**
   * Сохранить текущую версию в localStorage
   */
  setStoredVersion(version = this.currentVersion) {
    localStorage.setItem(this.storageKey, version)
  }

  /**
   * Проверить, изменилась ли версия приложения
   */
  hasVersionChanged() {
    const storedVersion = this.getStoredVersion()
    return storedVersion !== null && storedVersion !== this.currentVersion
  }

  /**
   * Очистить кеш приложения
   */
  async clearCache() {
    try {
      // Очистка localStorage (кроме важных данных)
      const importantKeys = ['user_token', 'user_data', 'app_settings']
      const keysToRemove = []
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && !importantKeys.includes(key) && key !== this.storageKey) {
          keysToRemove.push(key)
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key))

      // Очистка sessionStorage
      sessionStorage.clear()

      // Очистка кеша Service Worker (если есть)
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        for (const registration of registrations) {
          await registration.unregister()
        }
      }

      // Очистка кеша браузера (если поддерживается)
      if ('caches' in window) {
        const cacheNames = await caches.keys()
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        )
      }

      console.log('Кеш приложения очищен')
    } catch (error) {
      console.error('Ошибка при очистке кеша:', error)
    }
  }

  /**
   * Принудительное обновление страницы
   */
  forceReload() {
    // Добавляем timestamp для обхода кеша
    const timestamp = Date.now()
    const url = new URL(window.location.href)
    url.searchParams.set('v', this.currentVersion)
    url.searchParams.set('t', timestamp)
    
    window.location.href = url.toString()
  }

  /**
   * Проверить версию и обновить при необходимости
   */
  async checkAndUpdate() {
    try {
      if (this.hasVersionChanged()) {
        console.log(`Обнаружено обновление приложения: ${this.getStoredVersion()} → ${this.currentVersion}`)
        
        // Очищаем кеш
        await this.clearCache()
        
        // Сохраняем новую версию
        this.setStoredVersion()
        
        // Показываем уведомление пользователю
        this.showUpdateNotification()
        
        return true
      } else if (this.getStoredVersion() === null) {
        // Первый запуск приложения
        this.setStoredVersion()
        console.log(`Приложение запущено впервые. Версия: ${this.currentVersion}`)
      }
      
      return false
    } catch (error) {
      console.error('Ошибка при проверке версии:', error)
      return false
    }
  }

  /**
   * Показать уведомление об обновлении
   */
  showUpdateNotification() {
    // Создаем простое уведомление
    const notification = document.createElement('div')
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 15px 20px;
      border-radius: 5px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      z-index: 10000;
      font-family: Arial, sans-serif;
      font-size: 14px;
      max-width: 300px;
    `
    notification.innerHTML = `
      <strong>Приложение обновлено!</strong><br>
      Версия: ${this.currentVersion}<br>
      <small>Кеш очищен автоматически</small>
    `
    
    document.body.appendChild(notification)
    
    // Автоматически скрыть через 5 секунд
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification)
      }
    }, 5000)
  }

  /**
   * Получить информацию о версии
   */
  getVersionInfo() {
    return {
      current: this.currentVersion,
      stored: this.getStoredVersion(),
      hasChanged: this.hasVersionChanged(),
      buildTime: new Date().toISOString()
    }
  }
}

// Экспортируем singleton
export default new VersionService()